/**
 * Returns the reference "now" for a gig.
 * Demo gigs use gig.demoNow so they always render in pre-show state.
 * Real gigs use the actual current time.
 */
export function getReferenceTime(gig) {
  if (gig?.demoNow) return new Date(gig.demoNow);
  return new Date();
}

/**
 * Parse "HH:MM" time string into minutes since midnight.
 */
function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null;
  const [h, m] = timeStr.split(':').map(Number);
  if (isNaN(h) || isNaN(m)) return null;
  return h * 60 + m;
}

/**
 * The load-out row for the Day Timeline.
 *
 * The venue's own time (timeline.loadOutBy) is the answer whenever it is set. With none, no time
 * is made up: an estimate shown as "Load-Out Complete 22:30" reads as a fact the venue never
 * gave (it used to be curfew + 30 min, and overrode a venue's 23:00). The one thing still worked
 * out is a real conflict in what the venue did give: a hard stop 15 minutes or less after the
 * show ends leaves no room to load out.
 *
 * @param {object} timeline - { setEnd, loadOutBy }
 * @param {object} bookingTerms - { hardStop } (e.g. "21:00 — reason" or "21:00")
 * @returns {{ type: 'time', value: string } | { type: 'warning', label: string, subtext: string } | null}
 */
export function computeLoadOut(timeline, bookingTerms) {
  if (parseTimeToMinutes(timeline?.loadOutBy) != null) return { type: 'time', value: timeline.loadOutBy };

  const showEnd = parseTimeToMinutes(timeline?.setEnd);
  if (showEnd == null) return null;

  // A hard stop can run past midnight: 00:00 after a 23:30 show end is 30 minutes later, not
  // 23.5 hours earlier. Anything more than 6h before the show end is taken as the next day.
  const hardStopRaw = bookingTerms?.hardStop;
  let hardStop = hardStopRaw ? parseTimeToMinutes(hardStopRaw.split(/\s*[—–-]\s*/)[0].trim()) : null;
  if (hardStop != null && hardStop < showEnd - 360) hardStop += 1440;

  if (hardStop != null && hardStop <= showEnd + 15) {
    return { type: 'warning', label: 'Tight turnaround', subtext: 'Load-out overlaps hard stop' };
  }
  return null;
}
